import React, { useState, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { calculateVolumeCubicFeet, calculateNetCaseWeight, calculateGrossCaseWeight, formatDateOnly, formatQuantity, formatUnitCost } from '../utils/commonUtils'
import StockItemInfo from './StockItemInfo'
import { getCountryFlag } from '../utils/tableUtils'
import ResizableTable from './ResizableTable'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'

function InventoryList({ inventoryIds }) {
    const [inventoryItems, setInventoryItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [vendorMap, setVendorMap] = useState({})
    const [warehouseMap, setWarehouseMap] = useState({})
    const [locationMap, setLocationMap] = useState({})
    const [totalQuantity, setTotalQuantity] = useState(0)

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('inventoryItemColumnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            inventory_id: true,
            stock_item_id: false,
            sku: true,
            image: true,
            gtin: true,
            upc: false,
            description: true,
            lot: true,
            notes: false,
            bbDate: true,
            warehouse: true,
            location: true,
            categories: false,
            manufacturer: false,
            vendor: true,
            cost: true,
            itemSize: false,
            casePack: false,
            netCaseWeight: false,
            packageWeight: false,
            grossCaseWeight: false,
            ti: false,
            hi: false,
            countryOfOrigin: false,
            length: false,
            width: false,
            height: false,
            volume: false,
            quantity: true,
            in_transit: true
        };
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('inventoryItemColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            inventory_id: 'auto',
            stock_item_id: 'auto',
            image: 'auto',
            sku: 'auto',
            gtin: 'auto',
            upc: 'auto',
            description: 'auto',
            lot: 'auto',
            notes: 'auto',
            bbDate: 'auto',
            warehouse: 'auto',
            location: 'auto',
            categories: 'auto',
            manufacturer: 'auto',
            vendor: 'auto',
            cost: 'auto',
            itemSize: 'auto',
            casePack: 'auto',
            netCaseWeight: 'auto',
            packageWeight: 'auto',
            grossCaseWeight: 'auto',
            ti: 'auto',
            hi: 'auto',
            countryOfOrigin: 'auto',
            length: 'auto',
            width: 'auto',
            height: 'auto',
            volume: 'auto',
            quantity: 'auto',
            in_transit: 'auto'
        };
    });

    useEffect(() => {
        localStorage.setItem('inventoryItemColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    useEffect(() => {
        localStorage.setItem('inventoryItemColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        let isMounted = true;

        async function fetchInventoryItems() {
            try {
                console.log('InventoryList: Fetching items for IDs:', inventoryIds);
                setLoading(true);
                if (inventoryIds && inventoryIds.length > 0) {
                    const response = await inventoryItemAPI.getByIds(inventoryIds);
                    if (!isMounted) return;
                    
                    console.log('InventoryList: Fetched items:', response);
                    
                    // Extract the results array and total quantity from the response
                    const items = response?.results || [];
                    const total = response?.total_quantity || 0;
                    
                    setInventoryItems(items);
                    setTotalQuantity(total);
                }
            } catch (error) {
                if (!isMounted) return;
                console.error('InventoryList: Error fetching inventory items:', error);
                setInventoryItems([]);
                setTotalQuantity(0);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }
        fetchInventoryItems();

        return () => {
            isMounted = false;
        }
    }, [inventoryIds]);

    useEffect(() => {
        let isMounted = true;

        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                if (!isMounted) return;
                
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching vendors:', error)
            }
        }
        fetchVendors()

        return () => {
            isMounted = false;
        }
    }, [])

    useEffect(() => {
        let isMounted = true;

        async function fetchWarehouses() {
            try {
                const warehouses = await warehouseAPI.getAll()
                if (!isMounted) return;
                
                const mapping = warehouses.reduce((acc, warehouse) => {
                    acc[warehouse._id] = warehouse.name
                    return acc
                }, {})
                setWarehouseMap(mapping)
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching warehouses:', error)
            }
        }
        fetchWarehouses()

        return () => {
            isMounted = false;
        }
    }, [])

    useEffect(() => {
        let isMounted = true;

        async function fetchLocations() {
            try {
                // Get unique warehouse IDs from inventory items
                const warehouseIds = [...new Set(inventoryItems
                    .filter(item => item.warehouse && !item.transit)
                    .map(item => item.warehouse)
                )];
                
                if (warehouseIds.length === 0) {
                    setLocationMap({});
                    return;
                }

                // Create a single object to store all locations
                const locationsByWarehouse = {};
                
                // Fetch locations for all warehouses in parallel
                await Promise.all(warehouseIds.map(async (warehouseId) => {
                    if (warehouseId) {
                        const locations = await locationAPI.getAll(warehouseId);
                        if (!isMounted) return;
                        
                        locations.forEach(location => {
                            locationsByWarehouse[location._id] = location.name;
                        });
                    }
                }));

                if (!isMounted) return;
                setLocationMap(locationsByWarehouse);
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching locations:', error);
            }
        }
        fetchLocations();

        return () => {
            isMounted = false;
        }
    }, [inventoryItems]);

    return loading ? (
        <div className="text-center py-4">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        </div>
    ) : (
        <div className="table-responsive">
            {inventoryItems.length === 0 ? (
                <div className="alert alert-info">
                    No inventory items found for the provided IDs.
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="inventory_items_list"
                        columns={[
                            ...(columnVisibility.inventory_id ? [{
                                id: 'inventory_id',
                                header: 'Inventory ID',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit && (
                                            <small className="text-info">In Transit</small>
                                        )}
                                        <span>{row._id}</span>
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.stock_item_id ? [{
                                id: 'stock_item_id',
                                header: 'Stock Item ID',
                                cell: (row) => row.stock_item._id
                            }] : []),
                            ...(columnVisibility.sku ? [{
                                id: 'sku',
                                header: 'SKU',
                                cell: (row) => row.stock_item.sku
                            }] : []),
                            ...(columnVisibility.image ? [{
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.stock_item.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.stock_item} />
                                            </Tooltip>
                                        }
                                    >
                                        <img 
                                            src={row.stock_item.image} 
                                            alt="Stock Item Thumbnail"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            }] : []),
                            ...(columnVisibility.gtin ? [{
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => row.stock_item.gtin
                            }] : []),
                            ...(columnVisibility.upc ? [{
                                id: 'upc',
                                header: 'UPC',
                                cell: (row) => row.stock_item.upc
                            }] : []),
                            ...(columnVisibility.description ? [{
                                id: 'description',
                                header: 'Description',
                                cell: (row) => row.stock_item.description
                            }] : []),
                            ...(columnVisibility.lot ? [{
                                id: 'lot',
                                header: 'LOT',
                                cell: (row) => row.lot
                            }] : []),
                            ...(columnVisibility.notes ? [{
                                id: 'notes',
                                header: 'Notes',
                                cell: (row) => row.notes
                            }] : []),
                            ...(columnVisibility.bbDate ? [{
                                id: 'bbDate',
                                header: 'Best Before',
                                cell: (row) => formatDateOnly(row.bb_date)
                            }] : []),
                            ...(columnVisibility.warehouse ? [{
                                id: 'warehouse',
                                header: 'Warehouse',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.warehouse_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.location ? [{
                                id: 'location',
                                header: 'Location',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.location_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.vendor ? [{
                                id: 'vendor',
                                header: 'Vendor',
                                cell: (row) => vendorMap[row.vendor] || row.vendor || 'N/A'
                            }] : []),
                            ...(columnVisibility.manufacturer ? [{
                                id: 'manufacturer',
                                header: 'Manufacturer',
                                cell: (row) => vendorMap[row.stock_item.manufacturer] || row.stock_item.manufacturer || 'N/A'
                            }] : []),
                            ...(columnVisibility.cost ? [{
                                id: 'cost',
                                header: 'Cost',
                                cell: (row) => row.cost ? formatUnitCost(row.cost) : ''
                            }] : []),
                            ...(columnVisibility.itemSize ? [{
                                id: 'itemSize',
                                header: 'Item Size',
                                cell: (row) => `${row.stock_item.item_size} ${row.stock_item.item_size_units}`
                            }] : []),
                            ...(columnVisibility.casePack ? [{
                                id: 'casePack',
                                header: 'Case Pack',
                                cell: (row) => row.stock_item.case_pack
                            }] : []),
                            ...(columnVisibility.netCaseWeight ? [{
                                id: 'netCaseWeight',
                                header: 'Net Case Weight (lbs)',
                                cell: (row) => calculateNetCaseWeight(row.stock_item.item_size, row.stock_item.item_size_units, row.stock_item.case_pack)
                            }] : []),
                            ...(columnVisibility.packageWeight ? [{
                                id: 'packageWeight',
                                header: 'Package Weight',
                                cell: (row) => row.stock_item.package_weight && `${row.stock_item.package_weight}${row.stock_item.package_weight_units ? ` ${row.stock_item.package_weight_units}` : ' lbs'}`
                            }] : []),
                            ...(columnVisibility.grossCaseWeight ? [{
                                id: 'grossCaseWeight',
                                header: 'Gross Case Weight (lbs)',
                                cell: (row) => {
                                    const weight = calculateGrossCaseWeight(row.stock_item.item_size, 
                                        row.stock_item.item_size_units, row.stock_item.case_pack, row.stock_item.package_weight, row.stock_item.package_weight_units);
                                    return isNaN(weight) ? '' : weight;
                                }
                            }] : []),
                            ...(columnVisibility.ti ? [{
                                id: 'ti',
                                header: 'TI',
                                cell: (row) => row.stock_item.ti
                            }] : []),
                            ...(columnVisibility.hi ? [{
                                id: 'hi',
                                header: 'HI',
                                cell: (row) => row.stock_item.hi
                            }] : []),
                            ...(columnVisibility.countryOfOrigin ? [{
                                id: 'countryOfOrigin',
                                header: 'Country of Origin',
                                cell: (row) => row.stock_item.country_of_origin && (
                                    <>
                                        {getCountryFlag(row.stock_item.country_of_origin)}{' '}
                                        {row.stock_item.country_of_origin}{' '}
                                    </>
                                )
                            }] : []),
                            ...(columnVisibility.length ? [{
                                id: 'length',
                                header: 'L(inch)',
                                cell: (row) => row.stock_item.dimensions_l
                            }] : []),
                            ...(columnVisibility.width ? [{
                                id: 'width',
                                header: 'W(inch)',
                                cell: (row) => row.stock_item.dimensions_w
                            }] : []),
                            ...(columnVisibility.height ? [{
                                id: 'height',
                                header: 'H(inch)',
                                cell: (row) => row.stock_item.dimensions_h
                            }] : []),
                            ...(columnVisibility.volume ? [{
                                id: 'volume',
                                header: 'Volume',
                                cell: (row) => calculateVolumeCubicFeet(row.stock_item.dimensions_l, row.stock_item.dimensions_w, row.stock_item.dimensions_h)
                            }] : []),
                            ...(columnVisibility.quantity ? [{
                                id: 'quantity',
                                header: 'Quantity',
                                cell: (row) => formatQuantity(row.quantity)
                            }] : []),
                            ...(columnVisibility.in_transit ? [{
                                id: 'in_transit',
                                header: 'Transit',
                                cell: (row) => row.transit ? <i className="material-icons">local_shipping</i> : ''
                            }] : [])
                        ]}
                        data={inventoryItems}
                        initialColumnWidths={columnWidths}
                        striped
                        hover
                    />
                </>
            )}
        </div>
    )
}

export default InventoryList 